// components/Button/Button.js
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button - The universal button component
 * 
 * Semantic usage (recommended):
 *   <Button usage="cta">Get Started</Button>
 * 
 * Manual usage (for edge cases):
 *   <Button variant="outline" size="lg">Custom</Button>
 */

// Semantic presets
const USAGE_PRESETS = {
  cta: { variant: 'solid', size: 'lg' },
  'cta-secondary': { variant: 'primary', size: 'lg' },
  'learn-more': { variant: 'outline', size: 'md' },
  tertiary: { variant: 'ghost', size: 'md' },
  cancel: { variant: 'ghost', size: 'sm' },
  destructive: { variant: 'secondary', size: 'md' },
};

const Button = ({ 
  usage,                    // Semantic preset (optional)
  variant = 'primary',      // Style variant (fallback/manual)
  size = 'md',              // Size variant (fallback/manual)
  disabled = false,
  className = '',
  children,
  ...props 
}) => {
  // If usage preset provided, use it (but allow overrides)
  const finalVariant = usage && USAGE_PRESETS[usage] 
    ? USAGE_PRESETS[usage].variant 
    : variant;
    
  const finalSize = usage && USAGE_PRESETS[usage]
    ? USAGE_PRESETS[usage].size
    : size;
  
  // Build CSS classes
  const variantClass = {
    solid: 'btn-solid',
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    ghost: 'btn-ghost',
  }[finalVariant];
  
  const sizeClass = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
  }[finalSize];
  
  const buttonClasses = [
    'btn',
    variantClass,
    sizeClass,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <button
      className={buttonClasses}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  // Semantic usage (recommended)
  usage: PropTypes.oneOf([
    'cta',
    'cta-secondary',
    'learn-more',
    'tertiary',
    'cancel',
    'destructive',
  ]),
  
  // Manual control (fallback)
  variant: PropTypes.oneOf(['solid', 'primary', 'secondary', 'outline', 'ghost']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  
  // Standard props
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Button;